import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Play, Plus, RefreshCw01, Trash01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { formatBytes, formatDateTime } from "@/lib/format";
import {
  executeApiTool,
  fetchKbDictionaryContent,
  fetchTokenPassword,
  invalidPassword,
  useApiToolsConnectors,
  connectorIdForAuthServerType,
  useDeleteKbDictionary,
  useInvalidateKbDictionaries,
  useKbDictionaries,
  ADMIN_JWT_SENTINEL,
  type ApiToolsConnector,
  type ExecuteResponse,
  type KbDictionaryContent,
} from "@/lib/apiTools";
import { useAuthServerTypes } from "@/lib/connectors";
import { usePaatProfiles } from "@/lib/testActivation";
import { TokenPicker } from "@/components/app/TokenPicker";
import {
  connectorGroups,
  buildOperationBody,
  type FieldSpec,
  type OperationGroup,
  type OperationSpec,
} from "./apiTools/catalog";
import { Field, inputCls } from "./shared";
import { PrivatTokenPanel } from "./apiTools/PrivatTokenPanel";
import { PrivatPendingQueuePanel, pendingQueueAfter, EMPTY_PENDING_QUEUE, type PendingQueueState } from "./apiTools/PrivatPendingQueuePanel";
import { KbCaasWorkflowsPanel } from "./apiTools/KbCaasWorkflowsPanel";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import { cx } from "@/utils/cx";
import { Table } from "@/components/application/table/table";

type AuthPreset = "none" | "admin-jwt" | "apikey" | "bearer" | "custom-raw";

export function ApiToolsPage() {
  const { t } = useTranslation();
  const { data: connectors, isLoading } = useApiToolsConnectors();
  const [connectorId, setConnectorId] = useState<string | null>(null);

  const connector = connectors.find((c) => c.id === (connectorId ?? connectors[0]?.id));

  if (isLoading) return <LoadingIndicator />;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("apitools.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("apitools.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>

        {/* Connector tabs jako druhý potomek Rootu — dřív měly vlastní `border-b`,
            takže pod hlavičkou byly dvě linky nad sebou. Teď je dělící linka jen
            jedna (z Rootu) a taby jsou od nadpisu na `gap-5`. */}
        <div className="flex flex-wrap gap-2">
          {connectors.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setConnectorId(c.id)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                connector?.id === c.id ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </SectionHeader.Root>


      {connector && <ConnectorRunner key={connector.id} connector={connector} />}
    </div>
  );
}

function ConnectorRunner({ connector }: { connector: ApiToolsConnector }) {
  const { t } = useTranslation();
  const isEtts = connector.id === "etts";
  const groups = connectorGroups(connector.id);

  const [groupKey, setGroupKey] = useState<OperationGroup>("token");
  const group = groups.find((g) => g.group === groupKey) ?? groups[0];
  const [opId, setOpId] = useState(group.operations[0].id);
  const op = group.operations.find((o) => o.id === opId) ?? group.operations[0];

  // Prefill + nonce pro auto-workflow (create→process→verify/assemble): goToOp
  // remountuje OperationForm s předvyplněnými hodnotami.
  const [prefill, setPrefill] = useState<Record<string, unknown>>({});
  const [nonce, setNonce] = useState(0);

  // Fronta čekajících transakcí. Stav patří sem, ne do `OperationForm` — ten se při každém
  // přepnutí tabu i při každém `goToOp` remountuje (viz jeho `key`), takže by fronta zmizela
  // právě ve chvíli, kdy z ní uživatel odbavuje další položku.
  const [pending, setPending] = useState<PendingQueueState>(EMPTY_PENDING_QUEUE);

  // Ruční přepnutí na *jiný* tab vyprázdní formulář; klik na tab, na kterém uživatel právě
  // je, nesmí udělat nic. Bez téhle podmínky by po „Schválit" stačilo kliknout na zvýrazněný
  // tab („jsem tam, kde mám být?") a předvyplněná zpráva by beze slova zmizela.
  const selectGroup = (gk: OperationGroup) => {
    if (gk === groupKey) return;
    const g = groups.find((x) => x.group === gk)!;
    setGroupKey(gk);
    setOpId(g.operations[0].id);
    setPrefill({});
    setNonce((n) => n + 1);
  };
  const selectOp = (id: string) => {
    if (id === opId) return;
    setOpId(id);
    setPrefill({});
    setNonce((n) => n + 1);
  };
  const goToOp = (targetOpId: string, values: Record<string, unknown>) => {
    const g = groups.find((gr) => gr.operations.some((o) => o.id === targetOpId));
    if (g) setGroupKey(g.group);
    setOpId(targetOpId);
    setPrefill(values);
    setNonce((n) => n + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Group tabs */}
      <div className="flex flex-wrap gap-1">
        {groups.map((g) => (
          <button
            key={g.group}
            type="button"
            onClick={() => selectGroup(g.group)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
              groupKey === g.group ? "bg-secondary text-primary ring-1 ring-secondary" : "text-secondary hover:bg-primary_hover"
            }`}
          >
            {t(g.label)}
          </button>
        ))}
      </div>

      {/* Operation sub-tabs */}
      <div className="flex flex-wrap gap-1 border-t border-secondary pt-2">
        {group.operations.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => selectOp(o.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              opId === o.id ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
            }`}
          >
            {t(o.label)}
          </button>
        ))}
      </div>

      {connector.id === "privat" && <PrivatTokenPanel />}

      <OperationForm
        key={`${connector.id}:${op.id}:${nonce}`}
        connector={connector}
        op={op}
        isEtts={isEtts}
        initialValues={prefill}
        goToOp={goToOp}
        onResult={(opId, res, values) => setPending((p) => pendingQueueAfter(p, opId, res, values))}
      />

      {connector.id === "privat" && pending.tokenId && (
        <PrivatPendingQueuePanel
          connectorId={connector.id}
          tokenId={pending.tokenId}
          items={pending.items}
          onRefreshed={(items) => setPending((p) => ({ ...p, items }))}
          goToOp={goToOp}
        />
      )}

      {connector.id === "kb-caas" && <KbCaasWorkflowsPanel />}
      {connector.id === "kb-caas" && <KbDictionaryPanel />}
    </div>
  );
}

function OperationForm({
  connector,
  op,
  isEtts,
  initialValues,
  goToOp,
  onResult,
}: {
  connector: ApiToolsConnector;
  op: OperationSpec;
  isEtts: boolean;
  initialValues: Record<string, unknown>;
  goToOp: (opId: string, values: Record<string, unknown>) => void;
  /** Výsledek běhu pro konzumenty nad formulářem (fronta transakcí). */
  onResult?: (opId: string, res: ExecuteResponse, values: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const paat = usePaatProfiles();
  const invalidateKbDict = useInvalidateKbDictionaries();
  // Nabídka auth serverů = konektory tohoto buildu (sdílený hook, react-query dedupe).
  const authServerTypes = useAuthServerTypes();

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const f of op.fields) {
      if (f.default !== undefined) v[f.name] = f.kind === "checkbox" ? f.default === "true" : f.default;
    }
    return { ...v, ...initialValues };
  });
  const setVal = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }));

  // Konektor, podle kterého se gatují `connectorOnly` pole. Na eTTS loopback
  // záložce je cíl dynamický — určuje ho vybraný `authServerType`, takže i tady
  // dostaneme konektor-specifická pole (dřív šla vyplnit jen ručně přes obecný
  // key/value editor `connectorAttributes`).
  const effectiveConnectorId = isEtts
    ? connectorIdForAuthServerType(String(values.authServerType ?? ""))
    : connector.id;

  // Viditelná pole: ettsOnly/connectorOnly + showWhen (závislé na aktuálních hodnotách).
  const fields = op.fields.filter(
    (f) =>
      (!f.ettsOnly || isEtts) &&
      (!f.connectorOnly || f.connectorOnly === effectiveConnectorId) &&
      (!f.showWhen || String(values[f.showWhen.field] ?? "") === f.showWhen.value),
  );

  // `asTabs` pole se renderují nahoře jako přepínač režimu, zbytek zůstává v pořadí.
  const tabFields = fields.filter((f) => f.asTabs);
  const formFields = fields.filter((f) => !f.asTabs);

  // Auth (eTTS picker) / custom headers (connectors)
  const [authPreset, setAuthPreset] = useState<AuthPreset>("none");
  const [authCustom, setAuthCustom] = useState("");
  const [customHeaders, setCustomHeaders] = useState<{ name: string; value: string }[]>([]);
  const [includeStats, setIncludeStats] = useState(false);

  const [result, setResult] = useState<ExecuteResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (isEtts) {
      const auth = authorizationValue(authPreset, authCustom);
      if (auth) headers.Authorization = auth;
    } else {
      for (const h of customHeaders) if (h.name && h.value) headers[h.name] = h.value;
    }
    return headers;
  };

  const run = async () => {
    setValidationError(null);
    for (const f of fields) {
      const empty = f.kind === "connectorAttrs" || f.kind === "jsonArray" ? false : !String(values[f.name] ?? "").trim();
      if (f.required && empty) {
        setValidationError(t("apitools.required", { field: t(f.label) }));
        return;
      }
    }

    // Skupiny „aspoň jedno z" (`requiredOneOf`) — jen nad viditelnými poli, takže
    // se kontrola sama vypne, když je celá skupina skrytá jiným režimem.
    const oneOfGroups = new Map<string, FieldSpec[]>();
    for (const f of fields) {
      if (!f.requiredOneOf) continue;
      oneOfGroups.set(f.requiredOneOf, [...(oneOfGroups.get(f.requiredOneOf) ?? []), f]);
    }
    for (const groupFields of oneOfGroups.values()) {
      const anyFilled = groupFields.some((f) => String(values[f.name] ?? "").trim() !== "");
      if (!anyFilled) {
        setValidationError(t("apitools.requiredOneOf", { fields: groupFields.map((f) => t(f.label)).join(" / ") }));
        return;
      }
    }

    const body = buildOperationBody(fields, values);
    body.headers = buildHeaders();
    if (includeStats) body.includeStats = true;

    // passwordType → dořešit heslo z vybraného tokenu (async, buildBody je sync).
    const pt = fields.find((f) => f.kind === "passwordType");
    if (pt) {
      const real = await fetchTokenPassword(String(values.tokenId ?? ""));
      body.password = String(values[pt.name] ?? "valid") === "valid" ? real : invalidPassword(real);
    }
    // jsonArray → musí být neprázdné pole.
    const ja = fields.find((f) => f.kind === "jsonArray");
    if (ja && (!Array.isArray(body[ja.name]) || (body[ja.name] as unknown[]).length === 0)) {
      setValidationError(t("apitools.invalidJsonArray"));
      return;
    }

    setRunning(true);
    setResult(null);
    try {
      const res = await executeApiTool(connector.id, op.id, body);
      setResult(res);
      onResult?.(op.id, res, values);
      if (op.id === "kbCaasGetDictionary" && res.envelope.success) invalidateKbDict();
    } catch (e) {
      setResult({ envelope: { success: false, error: (e as Error).message }, stats: null, securityBlock: false, rawStatus: 0 });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Formulář */}
      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t(op.label)}</h2>

        {/* Režimová pole (`asTabs`) jdou nahoru jako taby — ve společném formuláři
            by se ztratila a nebylo by poznat, která pole k jakému režimu patří. */}
        {tabFields.map((f) => (
          <ModeTabs
            key={f.name}
            field={f}
            value={String(values[f.name] ?? f.default ?? "")}
            onChange={(v) => setVal(f.name, v)}
          />
        ))}

        {formFields.map((f) => (
          <FieldInput
            key={f.name}
            field={f}
            value={values[f.name]}
            onChange={(v) => setVal(f.name, v)}
            connectorType={connector.type}
            paat={paat.data ?? []}
            currentTokenId={String(values.tokenId ?? "")}
            connectorId={connector.id}
            authServerTypes={authServerTypes}
          />
        ))}

        {/* Auth / headers */}
        {isEtts ? (
          <AuthPicker preset={authPreset} custom={authCustom} onPreset={setAuthPreset} onCustom={setAuthCustom} />
        ) : (
          <CustomHeaders headers={customHeaders} onChange={setCustomHeaders} />
        )}

        <label className="flex items-center gap-2 text-sm text-primary">
          <input type="checkbox" checked={includeStats} onChange={(e) => setIncludeStats(e.target.checked)} />
          {t("apitools.includeStats")}
        </label>

        {validationError && <p className="text-sm text-error-primary">{validationError}</p>}

        <div>
          <Button size="lg" iconLeading={Play} isDisabled={running} onPress={() => void run()}>
            {running ? t("testing.running") : t("apitools.execute")}
          </Button>
        </div>
      </div>

      {/* Response viewer + workflow akce */}
      <div className="flex flex-col gap-4">
        <EnvelopeViewer result={result} />
        {result?.envelope.success && (
          <DrWorkflowActions
            op={op}
            envelopeResult={result.envelope.result}
            connector={connector}
            buildHeaders={buildHeaders}
            goToOp={goToOp}
            setResult={setResult}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Device-replacement auto-workflow: createDeviceReplacement → Approve/Reject
 * (Approve chain na approveDeviceReplacement + přepnutí na Process s předvyplněním),
 * processDeviceReplacement → předvyplnění Verify Activation / Assemble Sequences.
 */
function DrWorkflowActions({
  op,
  envelopeResult,
  connector,
  buildHeaders,
  goToOp,
  setResult,
}: {
  op: OperationSpec;
  envelopeResult: unknown;
  connector: ApiToolsConnector;
  buildHeaders: () => Record<string, string>;
  goToOp: (opId: string, values: Record<string, unknown>) => void;
  setResult: (r: ExecuteResponse) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const r = (envelopeResult ?? {}) as Record<string, unknown>;

  if (op.id === "createDeviceReplacement" && r.transactionId) {
    const tokenId = String(r.tokenId ?? "");
    const transactionId = String(r.transactionId);
    const message = String(r.message ?? "");
    const chain = async (operation: "approveDeviceReplacement" | "rejectDeviceReplacement") => {
      setBusy(true);
      try {
        const body: Record<string, unknown> = { tokenId, transactionId, headers: buildHeaders() };
        if (operation === "approveDeviceReplacement") body.message = message;
        const res = await executeApiTool(connector.id, operation, body);
        setResult(res);
        if (operation === "approveDeviceReplacement" && res.envelope.success) {
          goToOp("processDeviceReplacement", { tokenId, transactionId });
        }
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="flex flex-wrap gap-2 rounded-xl bg-primary p-4 ring-1 ring-secondary">
        <span className="w-full text-xs font-semibold text-tertiary">{t("apitools.dr.nextStep")}</span>
        <Button size="md" isDisabled={busy} onPress={() => void chain("approveDeviceReplacement")}>
          {t("apitools.dr.approve")}
        </Button>
        <Button size="md" color="secondary-destructive" isDisabled={busy} onPress={() => void chain("rejectDeviceReplacement")}>
          {t("apitools.dr.reject")}
        </Button>
      </div>
    );
  }

  if (op.id === "processDeviceReplacement" && (r.newActivationGuid || r.sequences)) {
    return (
      <div className="flex flex-wrap gap-2 rounded-xl bg-primary p-4 ring-1 ring-secondary">
        <span className="w-full text-xs font-semibold text-tertiary">{t("apitools.dr.nextStep")}</span>
        {r.newActivationGuid != null && (
          <Button
            size="md"
            color="secondary"
            onPress={() =>
              goToOp("verifyActivation", { activationGuid: String(r.newActivationGuid), authServerType: connector.type ?? "" })
            }
          >
            {t("apitools.dr.fillVerify")}
          </Button>
        )}
        {r.sequences != null && (
          <Button
            size="md"
            color="secondary"
            onPress={() => goToOp("assembleSequences", { sequences: JSON.stringify(r.sequences, null, 2) })}
          >
            {t("apitools.dr.fillAssemble")}
          </Button>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Přepínač režimu operace vykreslený jako taby (pole s `asTabs` v katalogu).
 * Vzhled odpovídá `underline` tabům UUI stejně jako `SectionTabs`, ale přepíná
 * hodnotu pole, ne routu — proto `<button>`, ne `<Link>`.
 */
function ModeTabs({ field, value, onChange }: { field: FieldSpec; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{t(field.label)}</span>
      <div
        role="tablist"
        aria-label={t(field.label)}
        className="relative flex gap-3 overflow-x-auto before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-border-secondary"
      >
        {(field.options ?? []).map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(o.value)}
              className={cx(
                "z-10 shrink-0 cursor-pointer border-b-2 px-0.5 pb-2.5 text-sm font-semibold whitespace-nowrap transition duration-100 ease-linear",
                active
                  ? "border-fg-brand-primary_alt text-brand-secondary"
                  : "border-transparent text-quaternary hover:border-fg-brand-primary_alt hover:text-brand-secondary",
              )}
            >
              {t(o.label)}
            </button>
          );
        })}
      </div>
      {field.hint && <span className="text-xs text-tertiary">{t(field.hint)}</span>}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  connectorType,
  paat,
  currentTokenId,
  connectorId,
  authServerTypes,
}: {
  field: FieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  /** `AuthServerType` tabu; `null` = eTTS loopback (bez omezení na konektor). */
  connectorType: string | null;
  paat: { id: string; profileName: string; platform: string }[];
  currentTokenId: string;
  connectorId: string;
  /** `AuthServerType` konektorů dostupných v tomto buildu. */
  authServerTypes: string[];
}) {
  const { t } = useTranslation();
  // Hvězdička i u `requiredOneOf` — skupina je povinná jako celek, konkrétní pole
  // v ní volitelné; kterou kombinaci lze použít, řekne hláška při odeslání.
  const label = `${t(field.label)}${field.required || field.requiredOneOf ? " *" : ""}`;
  const hint = field.hint ? t(field.hint) : undefined;

  switch (field.kind) {
    case "textarea":
      return (
        <Field label={label} hint={hint}>
          <textarea className={`${inputCls} h-24`} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case "number":
      return (
        <Field label={label} hint={hint}>
          <input className={inputCls} type="number" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm text-primary">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
      );
    case "select":
      return (
        <Field label={label} hint={hint}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label.includes(".") ? t(o.label) : o.label}
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    case "authServerType":
      return (
        <Field label={label} hint={hint}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            <option value="">{t("apitools.selectAuthServer")}</option>
            {authServerTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    case "tokenSelect":
      // Konektorové taby vidí jen tokeny svého konektoru; eTTS loopback (connectorType
      // null) všechny — cílový konektor si volí až samotná operace.
      return (
        <Field label={label} hint={hint}>
          <TokenPicker
            value={String(value ?? "")}
            onChange={onChange}
            authServerType={connectorType}
            activeOnly={field.activeOnly}
            placeholder={t("apitools.selectToken")}
          />
        </Field>
      );
    case "paatSelect":
      return (
        <Field label={label} hint={hint}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            <option value="">{t("testing.field.paatProfile.default")}</option>
            {paat.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id} — {p.profileName} ({p.platform})
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    case "tags":
      return (
        <Field label={label} hint={hint}>
          <input className={inputCls} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} placeholder="tag1, tag2" />
        </Field>
      );
    case "connectorAttrs":
      return <ConnectorAttrsEditor label={label} value={(value as Record<string, string>) ?? {}} onChange={onChange} />;
    case "jsonArray":
      return (
        <Field label={label} hint={hint}>
          <textarea
            className={`${inputCls} h-28 font-mono`}
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            placeholder='[ ... ]'
          />
        </Field>
      );
    case "passwordType":
      return (
        <Field label={label} hint={hint}>
          <div className="flex gap-4">
            {(["valid", "invalid"] as const).map((pt) => (
              <label key={pt} className="flex items-center gap-2 text-sm text-primary">
                <input
                  type="radio"
                  checked={String(value ?? "valid") === pt}
                  onChange={() => onChange(pt)}
                />
                {t(pt === "valid" ? "apitools.validPin" : "apitools.invalidPin")}
              </label>
            ))}
          </div>
        </Field>
      );
    case "pushId":
      return (
        <Field label={label} hint={hint}>
          <div className="flex gap-2">
            <input className={inputCls} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} placeholder="FCM push token" />
            <Button size="md" color="secondary" onPress={() => onChange(generatePushToken())}>
              {t("apitools.generate")}
            </Button>
          </div>
        </Field>
      );
    case "password":
      return (
        <PasswordField label={label} hint={hint} value={String(value ?? "")} onChange={onChange} tokenId={currentTokenId} />
      );
    case "otpVerify":
      return (
        <OtpVerifyField label={label} hint={hint} value={String(value ?? "")} onChange={onChange} tokenId={currentTokenId} connectorId={connectorId} />
      );
    default:
      return (
        <Field label={label} hint={hint}>
          <input className={inputCls} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
  }
}

function PasswordField({
  label,
  hint,
  value,
  onChange,
  tokenId,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: unknown) => void;
  tokenId: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const fill = async (valid: boolean) => {
    if (!tokenId) return;
    setBusy(true);
    try {
      const real = await fetchTokenPassword(tokenId);
      onChange(valid ? real : invalidPassword(real));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void fill(true)}>
          {t("apitools.validPin")}
        </Button>
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void fill(false)}>
          {t("apitools.invalidPin")}
        </Button>
      </div>
    </Field>
  );
}

/**
 * OTP pole pro verifyOtp s helper tlačítky, která přes `generateOtp` endpoint
 * vygenerují reálný (valid) nebo záměrně chybný (invalid) OTP a předvyplní pole
 * — mirror legacy `generateOtpForVerify()`.
 */
function OtpVerifyField({
  label,
  hint,
  value,
  onChange,
  tokenId,
  connectorId,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: unknown) => void;
  tokenId: string;
  connectorId: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const generate = async (valid: boolean) => {
    if (!tokenId) return;
    setBusy(true);
    setErr(null);
    try {
      const real = await fetchTokenPassword(tokenId);
      const password = valid ? real : invalidPassword(real);
      const res = await executeApiTool(connectorId, "generateOtp", { tokenId, password });
      const otp = (res.envelope.result as { otp?: string } | undefined)?.otp;
      if (res.envelope.success && otp) {
        onChange(otp);
      } else {
        setErr(res.envelope.error || res.envelope.message || t("common.loadError"));
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void generate(true)}>
          {t("apitools.genValidOtp")}
        </Button>
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void generate(false)}>
          {t("apitools.genInvalidOtp")}
        </Button>
      </div>
      {err && <p className="mt-1 text-xs text-error-primary">{err}</p>}
    </Field>
  );
}

function ConnectorAttrsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, string>;
  onChange: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  const rows = Object.entries(value);
  const update = (next: [string, string][]) => onChange(Object.fromEntries(next.filter(([k]) => k)));
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-secondary">{label}</span>
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <input
            className={inputCls}
            placeholder={t("apitools.attrKey")}
            value={k}
            onChange={(e) => {
              const next = [...rows] as [string, string][];
              next[i] = [e.target.value, v];
              update(next);
            }}
          />
          <input
            className={inputCls}
            placeholder={t("apitools.attrValue")}
            value={v}
            onChange={(e) => {
              const next = [...rows] as [string, string][];
              next[i] = [k, e.target.value];
              update(next);
            }}
          />
          <Button size="md" color="tertiary" iconLeading={Trash01} onPress={() => update(rows.filter((_, j) => j !== i) as [string, string][])}>
            {""}
          </Button>
        </div>
      ))}
      <div>
        <Button size="sm" color="secondary" iconLeading={Plus} onPress={() => update([...(rows as [string, string][]), ["", ""]])}>
          {t("apitools.addAttr")}
        </Button>
      </div>
    </div>
  );
}

function AuthPicker({
  preset,
  custom,
  onPreset,
  onCustom,
}: {
  preset: AuthPreset;
  custom: string;
  onPreset: (p: AuthPreset) => void;
  onCustom: (v: string) => void;
}) {
  const { t } = useTranslation();
  const showCustom = preset === "apikey" || preset === "bearer" || preset === "custom-raw";
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-secondary p-3 ring-1 ring-secondary">
      <Field label={t("apitools.authorization")}>
        <AppSelect value={preset} onChange={(e) => onPreset(e.target.value as AuthPreset)}>
          <option value="none">{t("apitools.auth.none")}</option>
          <option value="admin-jwt">{t("apitools.auth.adminJwt")}</option>
          <option value="apikey">{t("apitools.auth.apikey")}</option>
          <option value="bearer">{t("apitools.auth.bearer")}</option>
          <option value="custom-raw">{t("apitools.auth.customRaw")}</option>
        </AppSelect>
      </Field>
      {showCustom && (
        <input
          className={inputCls}
          value={custom}
          onChange={(e) => onCustom(e.target.value)}
          placeholder={preset === "apikey" ? "etts_kid_secret" : preset === "bearer" ? "eyJ..." : "ApiKey ..."}
        />
      )}
    </div>
  );
}

function CustomHeaders({
  headers,
  onChange,
}: {
  headers: { name: string; value: string }[];
  onChange: (h: { name: string; value: string }[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-secondary p-3 ring-1 ring-secondary">
      <span className="text-sm font-medium text-secondary">{t("apitools.customHeaders")}</span>
      {headers.map((h, i) => (
        <div key={i} className="flex gap-2">
          <input
            className={inputCls}
            placeholder={t("apitools.headerName")}
            value={h.name}
            onChange={(e) => onChange(headers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
          />
          <input
            className={inputCls}
            placeholder={t("apitools.headerValue")}
            value={h.value}
            onChange={(e) => onChange(headers.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
          />
          <Button size="md" color="tertiary" iconLeading={Trash01} onPress={() => onChange(headers.filter((_, j) => j !== i))}>
            {""}
          </Button>
        </div>
      ))}
      <div>
        <Button size="sm" color="secondary" iconLeading={Plus} onPress={() => onChange([...headers, { name: "", value: "" }])}>
          {t("apitools.addHeader")}
        </Button>
      </div>
    </div>
  );
}

function EnvelopeViewer({ result }: { result: ExecuteResponse | null }) {
  const { t } = useTranslation();
  if (!result) {
    return (
      <div className="flex items-center justify-center rounded-xl bg-primary p-5 text-sm text-tertiary ring-1 ring-secondary">
        {t("apitools.executeHint")}
      </div>
    );
  }

  const { envelope, stats, securityBlock } = result;

  if (securityBlock) {
    return (
      <div className="flex flex-col gap-2 rounded-xl bg-primary p-5 ring-1 ring-error">
        <h2 className="text-md font-semibold text-error-primary">{t("apitools.sessionStale.title")}</h2>
        <p className="text-sm text-secondary">{t("apitools.sessionStale.body")}</p>
      </div>
    );
  }

  const status = envelope.httpStatus;
  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex items-center gap-2">
        <Badge type="pill-color" size="sm" color={envelope.success ? "success" : "error"}>
          {envelope.success ? t("apitools.success") : t("apitools.error")}
        </Badge>
        {status != null && (
          <Badge type="pill-color" size="sm" color={status >= 200 && status < 300 ? "success" : "error"}>
            HTTP {status}
          </Badge>
        )}
      </div>

      {/* Request details */}
      {envelope.request && (
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex gap-2">
            <span className="font-mono font-semibold text-secondary">{envelope.request.method}</span>
            <span className="min-w-0 break-all text-secondary">{envelope.request.url}</span>
          </div>
          <Collapsible title={t("apitools.requestHeaders")} data={envelope.request.headers} />
          <Collapsible title={t("apitools.requestBody")} data={envelope.request.body} />
        </div>
      )}

      {/* Result / error */}
      {envelope.success ? (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("apitools.response")}</h4>
          <pre className="max-h-96 overflow-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {JSON.stringify(envelope.result, null, 2)}
          </pre>
        </div>
      ) : (
        <div className="rounded-lg bg-secondary p-3 text-sm ring-1 ring-error">
          <span className="text-error-primary">{envelope.error || envelope.message || t("common.loadError")}</span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3 text-xs text-tertiary">
            {stats.totalDurationMs != null && <span>{t("apitools.stats.total")}: {stats.totalDurationMs} ms</span>}
            {stats.connectorDurationMs != null && <span>{t("apitools.stats.connector")}: {stats.connectorDurationMs} ms</span>}
            {stats.processingDurationMs != null && <span>{t("apitools.stats.processing")}: {stats.processingDurationMs} ms</span>}
            {stats.apiCallCount != null && <span>{t("apitools.stats.calls")}: {stats.apiCallCount}</span>}
          </div>
          {stats.connectorCalls != null && stats.connectorCalls.length > 0 && (
            <Collapsible title={t("apitools.stats.connectorCalls")} data={stats.connectorCalls} />
          )}
        </div>
      )}
    </div>
  );
}

function Collapsible({ title, data }: { title: string; data: unknown }) {
  if (data == null) return null;
  return (
    <details className="rounded-md bg-secondary p-2 ring-1 ring-secondary">
      <summary className="cursor-pointer text-xs font-semibold text-tertiary">{title}</summary>
      <pre className="mt-1 max-h-48 overflow-auto font-mono text-xs text-secondary">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}

/** Náhodný FCM-like push token (~140 znaků) pro Generate tlačítko. */
function generatePushToken(): string {
  let s = "";
  while (s.length < 140) s += Math.random().toString(36).slice(2);
  return s.slice(0, 140);
}

function authorizationValue(preset: AuthPreset, custom: string): string | null {
  if (preset === "none") return null;
  if (preset === "admin-jwt") return ADMIN_JWT_SENTINEL;
  const v = custom.trim();
  if (!v) return null;
  if (preset === "apikey") return "ApiKey " + v;
  if (preset === "bearer") return "Bearer " + v;
  return v; // custom-raw
}

/** KB CAAS lokální dictionary cache — list + view + delete (jen kb-caas tab). */
function KbDictionaryPanel() {
  const { t } = useTranslation();
  const dicts = useKbDictionaries();
  const del = useDeleteKbDictionary();
  const [content, setContent] = useState<KbDictionaryContent | null>(null);
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);

  const view = async (version: number) => {
    if (content?.version === version) {
      setContent(null);
      return;
    }
    setLoadingVersion(version);
    try {
      setContent(await fetchKbDictionaryContent(version));
    } finally {
      setLoadingVersion(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-md font-semibold text-primary">{t("apitools.dict.title")}</h2>
        <Button size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={dicts.isFetching} onPress={() => void dicts.refetch()}>
          {t("apitools.dict.refresh")}
        </Button>
      </div>

      {dicts.isLoading && <LoadingIndicator />}
      {dicts.data && dicts.data.length === 0 && <p className="text-sm text-tertiary">{t("apitools.dict.empty")}</p>}

      {dicts.data && dicts.data.length > 0 && (
        <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
          <Table aria-label={t("apitools.dict.title")} selectionMode="none" size="sm">
            <Table.Header>
              <Table.Head isRowHeader id="version" label={t("apitools.dict.version")} />
              <Table.Head id="fetchedAt" label={t("apitools.dict.fetchedAt")} />
              <Table.Head id="size" label={t("apitools.dict.size")} />
              <Table.Head id="actions" label="" />
            </Table.Header>
            <Table.Body items={dicts.data}>
              {(d) => (
                <Table.Row id={d.version}>
                  <Table.Cell className="font-mono text-secondary">{d.version}</Table.Cell>
                  <Table.Cell className="text-tertiary">{formatDateTime(d.fetchedAt)}</Table.Cell>
                  <Table.Cell className="text-tertiary">{formatBytes(d.contentLength)}</Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        color="tertiary"
                        iconLeading={Eye}
                        isDisabled={loadingVersion === d.version}
                        onPress={() => void view(d.version)}
                      >
                        {t("apitools.dict.view")}
                      </Button>
                      <Button
                        size="sm"
                        color="tertiary-destructive"
                        iconLeading={Trash01}
                        isDisabled={del.isPending}
                        onPress={() => {
                          if (content?.version === d.version) setContent(null);
                          del.mutate(d.version);
                        }}
                      >
                        {t("common.delete")}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {content && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">
            {t("apitools.dict.version")} {content.version}
          </h4>
          <pre className="max-h-96 overflow-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {content.content}
          </pre>
        </div>
      )}
    </div>
  );
}
